import Vue from 'vue';
import Vuex from 'vuex';
import backendService from '@/services/backend-service.js';
import interactStore from '@/stores/interact-store';
import Decimal from 'decimal.js';
Vue.use(Vuex);

export default new Vuex.Store({
	modules: {
		interactStore,
	},
	state: {
		cart: {},
		cartCount: 0,
		dropdownToOptions: {},
		enumToOption: {},
		expenses: [],
		idToOption: {},
		currentUser: {},
		isLoggedIn: false,
		isAdmin: false,
		isRoot: false,
		isUpdatingCart: false,
		stores: [],
		search: '',
		orderSearch: '',
		customers: [],
		invoices: [],
		orders: [],
		shipments: [],
		shipmentGroups: [],
		stocks: [],
		toUpdateOrder: {},
		users: [],
		vendors: [],
	},
	getters: {
		cart: (state) => state.cart,
		cartCount: (state) => state.cartCount,
		dropdownToOptions: (state) => state.dropdownToOptions,
		idToOption: (state) => state.idToOption,
		enumToOption: (state) => state.enumToOption,
		currentUser: (state) => state.currentUser,
		isLoggedIn: (state) => state.isLoggedIn,
		isAdmin: (state) => state.isAdmin,
		isRoot: (state) => state.isRoot,
		isUpdatingCart: (state) => state.isUpdatingCart,
		stores: (state) => state.stores,
		customers: (state) => state.customers,
		expenses: (state) => state.expenses,
		orders: (state) => state.orders,
		shipmentGroups: (state) => state.shipmentGroups,
		shipments: (state) => state.shipments,
		stocks: (state) => state.stocks,
		search: (state) => state.search,
		invoices: (state) => state.invoices,
		toUpdateOrder: (state) => state.toUpdateOrder,
		orderSearch: (state) => state.orderSearch,
		users: (state) => state.users,
		vendors: (state) => state.vendors,
	},
	mutations: {
		addCustomer(state, payload) {
			state.customers.unshift(payload);
		},
		addOrder(state, payload) {
			state.orders.unshift(payload);
		},
		addShipment(state, payload) {
			state.shipments.unshift(payload);
		},
		addShipmentGroup(state, payload) {
			state.shipmentGroups.unshift(payload);
		},
		addVendor(state, payload) {
			state.vendors.unshift(payload);
		},
		resetCurrentUser(state) {
			state.currentUser = {};
			state.isLoggedIn = false;
			state.isAdmin = false;
			state.isRoot = false;
		},
		setCart(state, payload) {
			state.cart = payload;
		},
		setCartCount(state, payload) {
			state.cartCount = payload;
		},
		setDropdownToOptions(state, payload) {
			state.dropdownToOptions = payload;
			for (let key in payload) {
				let options = payload[key].options;
				options.forEach((option) => {
					state.idToOption[option.id] = option;
					if (option.customEnum)
						state.enumToOption[option.customEnum] = option;
				});
			}
		},
		setCurrentUser(state, payload) {
			state.currentUser = payload;
			state.isLoggedIn = true;
			state.isAdmin = state.currentUser.isAdmin || state.currentUser.IsAdmin;
			state.isRoot = state.currentUser.isRoot || state.currentUser.IsRoot;
		},
		setExpenseFilter(state, payload) {
			state.expenseFilter = payload;
		},
		setInvoices(state, payload) {
			let invoices = payload;
			for (let i = 0; i < invoices.length; i++) {
				let invoice = invoices[i];
				let sum = new Decimal(invoice.amountPaid).plus(
					new Decimal(invoice.creditPaid)
				);
				let totalAmount = new Decimal(invoice.totalAmount);
				if (totalAmount.minus(sum) > 0) {
					Vue.set(invoice, '_amountDue', totalAmount.minus(sum));
				}
			}

			invoices.forEach((e) => Vue.set(e, '_selected', false));
			state.invoices = payload;
		},
		setIsLoggedIn(state, payload) {
			state.isLoggedIn = payload;
		},
		setIsAdmin(state, payload) {
			state.isAdmin = payload;
		},

		setIsUpdatingCart(state, payload) {
			state.isUpdatingCart = payload;
		},
		setOrders(state, payload) {
			state.orders = payload;
		},
		setSearch(state, payload) {
			state.search = payload;
		},
		setOrderSearch(state, payload) {
			state.orderSearch = payload;
		},
		setStores(state, payload) {
			state.stores = payload;
		},
		setCustomers(state, payload) {
			state.customers = payload;
		},
		setExpenses(state, payload) {
			state.expenses = payload;
		},
		setShipments(state, payload) {
			state.shipments = payload;
		},
		setShipmentGroups(state, payload) {
			state.shipmentGroups = payload;
		},
		setToUpdateOrder(state, payload) {
			state.toUpdateOrder = payload;
		},
		setUsers(state, payload) {
			state.users = payload;
		},
		setVendors(state, payload) {
			state.vendors = payload;
		},
		setStocks(state, payload) {
			state.stocks = payload;
		},
		updateCustomer(state, payload) {
			let idx = state.customers.findIndex((x) => x.id == payload.id);
			state.customers[idx] = payload;
		},
		updateShipment(state, payload) {
			let idx = state.shipments.findIndex((x) => x.id == payload.id);
			state.shipments[idx] = payload;
		},
		updateShipmentGroup(state, payload) {
			let idx = state.shipmentGroups.findIndex((x) => x.id == payload.id);
			state.shipmentGroups[idx] = payload;
		},
	},
	actions: {
		getDropdownToOptions(context) {
			backendService.getDropdownToOptions().then((response) => {
				context.commit('setDropdownToOptions', response.data);
			});
		},
		getStoresByUserId(context) {
			backendService
				.getStoresByUserId(context.state.currentUser.id)
				.then((response) => {
					context.commit('setStores', response.data);
				});
		},
		getCustomers(context) {
			backendService.getCustomers().then((response) => {
				context.commit('setCustomers', response.data);
			});
		},
		getExpenses(context) {
			backendService.getExpenses().then((response) => {
				context.commit('setExpenses', response.data);
			});
		},
		getInvoices(context) {
			backendService.getInvoices().then((response) => {
				context.commit('setInvoices', response.data);
			});
		},
		getOrders(context) {
			backendService.getMinimalOrders().then((response) => {
				context.commit('setOrders', response.data);
			});
		},
		getShipments(context) {
			backendService.getShipments().then((response) => {
				context.commit('setShipments', response.data);
			});
		},
		getShipmentGroups(context) {
			backendService.getShipmentGroups().then((response) => {
				context.commit('setShipmentGroups', response.data);
			});
		},
		getStocks(context) {
			backendService.getStocks().then((response) => {
				context.commit('setStocks', response.data);
			});
		},
		getUsers(context) {
			backendService.getUsers().then((response) => {
				context.commit('setUsers', response.data);
			});
		},
		getVendors(context) {
			backendService.getVendors().then((response) => {
				context.commit('setVendors', response.data);
			});
		},
	},
});
